import Promise from 'bluebird';
import { useMemo } from 'react';
import type { RequestConfig } from '../public-types';

type Action = (..._args: never[]) => void;
type DropLast<TParameters extends unknown[]> =
  TParameters extends [...infer TLeading, unknown] ? TLeading : [];
type PromisifiedAction<TAction extends Action> =
  (..._args: DropLast<Parameters<TAction>>) => Promise<unknown>;
type PromisifiedActions<TActions extends readonly Action[]> = {
  [TIndex in keyof TActions]: PromisifiedAction<TActions[TIndex]>;
};

export function usePromisify<TActions extends readonly Action[]>(
  actions: TActions,
): PromisifiedActions<TActions> {
  return useMemo(() => actions.map(action => (...args: DropLast<Parameters<typeof action>>) =>
    new Promise((resolve, reject) => {
      const configBuilder = <TResult>(config: RequestConfig<TResult>) => ({
        ...config,
        onComplete: (processedResponse: TResult) => {
          config.onComplete?.(processedResponse);
          resolve(processedResponse);
        },
        onError: (error: unknown) => {
          config.onError?.(error);
          reject(error);
        },
      });
      const mergedArgs = [...args, configBuilder] as Parameters<typeof action>;
      action(...mergedArgs);
    })) as PromisifiedActions<TActions>, actions); // eslint-disable-line react-hooks/exhaustive-deps
}
